import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, ArrowLeft, Box, ClipboardList, Target } from 'lucide-react';
import type { StoreProfileDetail } from '../../types';
import { TwinArea } from '../areas/TwinArea';
import { OperationsArea } from '../areas/OperationsArea';
import { RecordArea } from '../areas/RecordArea';
import { ManageTwinHeader } from './ManageTwinHeader';

interface ManageTwinWorkspaceProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam: string;
  onExit: () => void;
}

/**
 * Administrator configuration mode — all setup, mapping and publish controls
 * live here, visually distinct from the retailer experience.
 */
export function ManageTwinWorkspace({
  profile,
  storeId,
  storeIdParam,
  onExit,
}: ManageTwinWorkspaceProps) {
  const [activeArea, setActiveArea] = useState('setup');
  const [twinSubTab, setTwinSubTab] = useState<string | null>(null);
  const [focusedAisleNumber, setFocusedAisleNumber] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);

  const handleExit = () => {
    if (dirty) {
      const confirmed = window.confirm('You have unsaved changes. Exit Manage Twin anyway?');
      if (!confirmed) return;
    }
    onExit();
  };

  const stats = profile.evidenceMappingStats;
  const pendingTotal = stats.needsReview + stats.unplaced;

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/30 p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            Manage Twin — editing mode
          </div>
          <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            Configure the store digital twin, place evidence on the floorplan, and make it available to retailers when ready.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={handleExit} className="bg-white">
          <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden />
          Exit Manage Twin
        </Button>
      </div>

      <ManageTwinHeader profile={profile} storeIdParam={storeIdParam} />

      <Tabs value={activeArea} onValueChange={setActiveArea}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-white/80 p-1 mb-4">
          <TabsTrigger value="setup" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-950">
            <Box className="h-4 w-4 mr-1" aria-hidden />
            Twin setup
            {pendingTotal > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-200 text-amber-900 text-[10px] px-1.5">
                {pendingTotal}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="operations" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-950">
            <Target className="h-4 w-4 mr-1" aria-hidden />
            Campaigns & media
          </TabsTrigger>
          <TabsTrigger value="record" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-950">
            <ClipboardList className="h-4 w-4 mr-1" aria-hidden />
            Store record
          </TabsTrigger>
        </TabsList>

        <TabsContent value="setup">
          <TwinArea
            profile={profile}
            storeId={storeId}
            storeIdParam={storeIdParam}
            focusedAisleNumber={focusedAisleNumber}
            onFocusAisle={setFocusedAisleNumber}
            requestedSubTab={twinSubTab}
            onSubTabChange={() => setTwinSubTab(null)}
            onDirtyChange={setDirty}
            manageMode
          />
        </TabsContent>

        <TabsContent value="operations">
          <OperationsArea profile={profile} storeId={storeId} storeIdParam={storeIdParam} />
        </TabsContent>

        <TabsContent value="record">
          <RecordArea profile={profile} storeId={storeId} storeIdParam={storeIdParam} manageMode />
        </TabsContent>
      </Tabs>
    </div>
  );
}
